import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { useAiChat } from '@factorialco/f0-react'
import { Plus, Pencil, Delete } from '@factorialco/f0-react/icons/app'
import {
  IncentivesCompensationPlanDetailDocument,
  IncentivesCompensationPlanDetailMetricsDocument,
  IncentivesCompensationPlanDetailMetricFragment,
} from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useOneMutationSubscription } from 'modules/ai/lib/useOneMutationSubscription'
import { getPayoutRuleSummary } from 'modules/incentives/lib/payoutRules'
import i18n from 'modules/core/lib/i18n'
import { compact } from 'lodash'

interface CompensationPlanMetricsProps {
  periodId: string
  planName: string
  periodLabel: string
  canCreateMetric: boolean
}

export function CompensationPlanMetrics({
  periodId,
  planName,
  periodLabel,
  canCreateMetric,
}: CompensationPlanMetricsProps) {
  const apolloClient = useApolloClient()

  const refetchMetrics = async () => {
    await apolloClient.refetchQueries({
      include: [
        IncentivesCompensationPlanDetailMetricsDocument,
        IncentivesCompensationPlanDetailDocument,
      ],
    })
  }

  useOneMutationSubscription('AddIncentivePlanMetrics', refetchMetrics)
  useOneMutationSubscription('RemoveIncentivePlanMetrics', refetchMetrics)
  useOneMutationSubscription('UpdateIncentivePlanMetrics', refetchMetrics)

  const aiChat = useAiChat()

  const openOne = (message: string) => {
    if (aiChat.enabled) {
      aiChat.setOpen(true)
      aiChat.sendMessage(message)
    }
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: IncentivesCompensationPlanDetailMetricsDocument,
      perPage: 20,
      mapData: (data) => data.incentives.compensationPlanMetricsConnection.nodes || [],
      mapPagination: (data) => ({
        total: data.incentives.compensationPlanMetricsConnection.totalCount ?? 0,
      }),
      mapVariables: () => ({
        periodIds: [periodId],
      }),
      secondaryActions: {
        expanded: 1,
        actions: () =>
          compact([
            canCreateMetric && {
              label: i18n.t('incentives.detail.metrics.add_metric.label'),
              icon: Plus,
              onClick: () =>
                openOne(
                  i18n.t('incentives.detail.metrics.add_metric.one_prompt', {
                    planName,
                    periodLabel,
                  })
                ),
            },
          ]),
      },
      itemActions: (item: IncentivesCompensationPlanDetailMetricFragment) => {
        const goalName =
          item.compensableMetric?.__typename === 'GoalsMetricGroup'
            ? item.compensableMetric.title
            : ''

        return compact([
          item._policies?.update.can && {
            label: i18n.t('incentives.detail.metrics.edit_metric.label'),
            icon: Pencil,
            onClick: () =>
              openOne(
                i18n.t('incentives.detail.metrics.edit_metric.one_prompt', {
                  planName,
                  goalName,
                  periodLabel,
                })
              ),
          },
          item._policies?.delete.can && {
            label: i18n.t('incentives.detail.metrics.remove_metric.label'),
            icon: Delete,
            critical: true,
            onClick: () =>
              openOne(
                i18n.t('incentives.detail.metrics.remove_metric.one_prompt', {
                  planName,
                  goalName,
                  periodLabel,
                })
              ),
          },
        ])
      },
    },
    [canCreateMetric, periodId]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('incentives.detail.metrics.list.name'),
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => {
                  const compensableMetric = metric.compensableMetric
                  if (compensableMetric?.__typename === 'GoalsMetricGroup') {
                    return {
                      type: 'text' as const,
                      value: compensableMetric.title,
                    }
                  }

                  return undefined
                },
              },
              {
                label: i18n.t('incentives.detail.metrics.list.progress'),
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => {
                  const compensableMetric = metric.compensableMetric
                  if (
                    compensableMetric?.__typename === 'GoalsMetricGroup' &&
                    typeof compensableMetric.summary?.averageProgress === 'number'
                  ) {
                    return {
                      type: 'progressBar' as const,
                      value: {
                        value: compensableMetric?.summary?.averageProgress,
                        label: `${i18n.formatNumber(compensableMetric?.summary?.averageProgress)}%`,
                      },
                    }
                  }

                  return {
                    type: 'text' as const,
                    value: '-',
                  }
                },
              },
              {
                label: i18n.t('incentives.detail.metrics.list.weight'),
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => ({
                  type: 'dotTag' as const,
                  value: {
                    label: `${i18n.formatNumber(metric.weight)}%`,
                    color: 'grey' as const,
                  },
                }),
              },
              {
                label: i18n.t('incentives.detail.metrics.list.threshold'),
                align: 'right' as const,
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => {
                  const { threshold } = getPayoutRuleSummary(metric.payoutRules)

                  return {
                    type: 'text' as const,
                    value: threshold !== null ? `${i18n.formatNumber(threshold)}%` : '-',
                  }
                },
              },
              {
                label: i18n.t('incentives.detail.metrics.list.cap'),
                align: 'right' as const,
                render: (metric: IncentivesCompensationPlanDetailMetricFragment) => {
                  const { cap } = getPayoutRuleSummary(metric.payoutRules)

                  return {
                    type: 'text' as const,
                    value: cap !== null ? `${i18n.formatNumber(cap)}%` : '-',
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
